import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress
} from '@mui/material';
import { 
  Analytics, 
  TrendingUp, 
  TrendingDown, 
  Timeline,
  Person,
  CalendarToday,
  AttachMoney 
} from '@mui/icons-material';
import './ForecastModals.css';

const VarianceAnalysisModal = ({ open, onClose }) => {
  const [analysisData, setAnalysisData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('7'); // Days
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [staffList, setStaffList] = useState([]);

  useEffect(() => {
    if (open) {
      loadVarianceAnalysis();
    }
  }, [open, timeRange, selectedStaff]);

  const loadVarianceAnalysis = async () => {
    setLoading(true);
    setError('');
    
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(timeRange));
      const startDateStr = startDate.toISOString().split('T')[0];
      
      const response = await fetch(
        `/api/forecast/variance-analysis?start_date=${startDateStr}&end_date=${endDate}${
          selectedStaff !== 'all' ? `&staff_id=${selectedStaff}` : ''
        }`
      );
      
      const data = await response.json();
      
      if (data.success) {
        setAnalysisData(data.variance_data);
        setSummary(data.summary);
        
        // Extract unique staff for filter
        const uniqueStaff = [...new Set(data.variance_data.map(item => ({
          id: item.staff_member_id,
          name: item.staff_name
        })))];
        setStaffList(uniqueStaff);
      } else {
        setError(data.message || 'Failed to load variance analysis');
      }
    } catch (err) {
      setError('Error loading variance analysis');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const getVarianceColor = (variance) => {
    if (variance > 50) return 'success';
    if (variance > 0) return 'info';
    if (variance > -50) return 'warning';
    return 'error';
  };

  const getAccuracyPercentage = (actual, forecast) => {
    if (forecast === 0) return 0;
    return Math.max(0, 100 - (Math.abs(actual - forecast) / forecast * 100));
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      className="forecast-modal-overlay"
    >
      <DialogTitle className="forecast-modal-title">
        <Box display="flex" alignItems="center" gap={2}>
          <Analytics />
          <div>
            <Typography variant="h6">Variance Analysis Dashboard</Typography>
            <Typography variant="body2" color="textSecondary">
              Dynamic Forecasting Business Intelligence
            </Typography>
          </div>
        </Box>
      </DialogTitle>

      <DialogContent className="forecast-modal-content">
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Controls */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                label="Time Range"
              >
                <MenuItem value="7">Last 7 days</MenuItem>
                <MenuItem value="14">Last 2 weeks</MenuItem>
                <MenuItem value="30">Last 30 days</MenuItem>
                <MenuItem value="90">Last 3 months</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Staff Member</InputLabel>
              <Select
                value={selectedStaff}
                onChange={(e) => setSelectedStaff(e.target.value)}
                label="Staff Member"
              >
                <MenuItem value="all">All Staff</MenuItem>
                {staffList.map(staff => (
                  <MenuItem key={staff.id} value={staff.id}>{staff.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {loading ? (
          <Box>
            <Typography>Loading variance analysis...</Typography>
            <LinearProgress sx={{ mt: 1 }} />
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Card className="variance-summary-card">
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarToday color="primary" />
                      <Typography variant="h6" color="primary">
                        {formatCurrency(summary.total_original_forecast)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      Original Sunday Forecasts
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card className="variance-summary-card">
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Timeline color="success" />
                      <Typography variant="h6" color="success.main">
                        {formatCurrency(summary.total_booking_changes)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      Booking Changes
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card className="variance-summary-card">
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TrendingUp color="info" />
                      <Typography variant="h6" color="info.main">
                        {formatCurrency(summary.total_walk_ins)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      Walk-ins & Same-day
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card className="variance-summary-card">
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1}>
                      <AttachMoney color="secondary" />
                      <Typography variant="h6" color="secondary.main">
                        {summary.forecast_accuracy?.toFixed(1)}%
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      Forecast Accuracy
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Business Intelligence Insights */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="subtitle2">🧠 Business Intelligence Insights</Typography>
              <Typography variant="body2">
                <strong>Booking Changes:</strong> Shows pre-planning vs actual booking patterns. 
                Positive = more bookings added, Negative = cancellations received.
                <br />
                <strong>Walk-ins:</strong> Revenue above/below latest forecasts = same-day decisions and walk-in clients.
                <br />
                <strong>Total Variance:</strong> Original Sunday forecast vs actual = complete picture of weekly planning accuracy.
              </Typography>
            </Alert>

            {/* Detailed Data Table */}
            <Typography variant="h6" sx={{ mb: 2 }}>Detailed Variance Analysis</Typography>
            <TableContainer component={Paper} className="variance-table">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Staff</TableCell>
                    <TableCell align="right">Original</TableCell>
                    <TableCell align="right">Latest</TableCell>
                    <TableCell align="right">Actual</TableCell>
                    <TableCell align="center">Booking Changes</TableCell>
                    <TableCell align="center">Walk-ins</TableCell>
                    <TableCell align="center">Total Variance</TableCell>
                    <TableCell align="center">Accuracy</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analysisData.map((row, index) => {
                    const accuracy = getAccuracyPercentage(row.actual_revenue, row.latest_forecast);
                    
                    return (
                      <TableRow key={index} hover>
                        <TableCell>{formatDate(row.date)}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Person fontSize="small" />
                            {row.staff_name}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{formatCurrency(row.original_forecast)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.latest_forecast)}</TableCell>
                        <TableCell align="right">
                          <strong>{formatCurrency(row.actual_revenue)}</strong>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={formatCurrency(row.booking_changes)}
                            color={row.booking_changes >= 0 ? 'success' : 'warning'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={formatCurrency(row.walk_ins)}
                            color={getVarianceColor(row.walk_ins)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={formatCurrency(row.total_variance)}
                            color={getVarianceColor(row.total_variance)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" gap={1}>
                            {accuracy >= 90 ? <TrendingUp color="success" /> : 
                             accuracy >= 70 ? <Timeline color="warning" /> : 
                             <TrendingDown color="error" />}
                            <Typography variant="body2">
                              {accuracy.toFixed(0)}%
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions className="forecast-modal-actions">
        <Button onClick={onClose}>
          Close
        </Button>
        <Button
          variant="outlined"
          onClick={loadVarianceAnalysis}
          disabled={loading}
        >
          Refresh Analysis
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VarianceAnalysisModal;